"use client";

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { Language, getTranslations } from '@/lib/i18n';

interface HeroSectionProps {
  language: Language;
}

export default function HeroSection({ language }: HeroSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const t = getTranslations(language);
  const h = t.home.hero;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className="min-h-screen flex flex-col lg:flex-row items-center gap-12 lg:gap-20 px-6 md:px-12 py-24 lg:py-32 max-w-7xl mx-auto">
      {/* Left Content */}
      <div className="flex-1 w-full flex flex-col items-start space-y-8 animate-fade-in">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse-dot"></div>
          <span className="text-sm font-medium text-foreground uppercase tracking-wider">
            {h.badge}
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight font-serif">
          <span className="block text-muted-foreground font-light">{h.headlineLine1}</span>
          <span className="block font-bold text-primary">{h.headlineLine2}</span>
          <span className="block font-light italic">{h.headlineLine3}</span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
          {h.subheadline}
        </p>

        {/* Cuisine Badges */}
        <div className="flex flex-wrap gap-3">
          {h.cuisines?.map((cuisine, index) => (
            <span
              key={`cuisine_${index}`}
              className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-semibold uppercase tracking-wider hover:bg-accent/20 transition-colors cursor-default">
              {cuisine}
            </span>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <Link
            href="#reservation"
            className="px-8 py-4 bg-primary text-primary-foreground rounded-full text-base font-bold uppercase tracking-wider hover:scale-105 active:scale-95 transition-transform shadow-xl text-center">
            {h.reserveTable}
          </Link>
          <Link
            href="/menu"
            className="px-8 py-4 border-2 border-primary text-primary rounded-full text-base font-bold uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-all text-center">
            {h.exploreMenu}
          </Link>
        </div>

        {/* Availability Badge */}
        <div className="flex items-center gap-3 pt-4">
          <Icon name="ClockIcon" size={20} className="text-success" />
          <span className="text-sm font-medium text-muted-foreground">
            <span className="text-success font-bold">8 {h.tablesAvailable}</span>
          </span>
        </div>
      </div>

      {/* Right Visual Composition */}
      <div className="flex-1 w-full relative flex items-center justify-center lg:justify-end py-10 lg:py-0 animate-fade-in delay-200">
        <div className="relative w-full max-w-md aspect-[3/4]">
          {/* Main Arched Image */}
          <div className="absolute inset-0 rounded-t-[10rem] rounded-b-[2rem] overflow-hidden shadow-2xl group">
            <AppImage
              src="https://images.unsplash.com/photo-1725826467877-dd337f7c1712"
              alt="Elegant restaurant table setting with gourmet dishes and wine glasses in warm ambient lighting"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="gradient-overlay-bottom"></div>

            {/* Floating Badge on Image */}
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between text-primary-foreground z-10">
              <div>
                <p className="text-sm font-medium opacity-80 mb-1">{h.featuredDish}</p>
                <p className="text-2xl font-serif font-light">{h.signaturePasta}</p>
              </div>
              <button className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-colors">
                <Icon name="ArrowRightIcon" size={24} />
              </button>
            </div>
          </div>

          {/* Floating Menu Card */}
          <div className="absolute -bottom-12 -left-4 md:-left-12 w-72 bg-card shadow-2xl rounded-3xl overflow-hidden transform rotate-[-3deg] hover:rotate-0 transition-transform duration-500 z-20 border border-border/50">
            <div className="p-5 border-b border-border/50 flex justify-between items-start">
              <div>
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-1">
                  {h.todaysSpecial}
                </p>
                <p className="font-serif text-xl text-foreground leading-tight">
                  {h.chefsTastingMenu}
                </p>
              </div>
              <Icon name="SparklesIcon" size={24} className="text-accent" />
            </div>

            <div className="p-5 bg-card relative">
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{h.price}</p>
                  <p className="text-sm font-medium text-foreground">{h.priceValue}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{h.courses}</p>
                  <p className="text-sm font-medium text-foreground">{h.coursesValue}</p>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-success/10 text-success text-xs font-semibold uppercase tracking-wider">
                  {h.available}
                </span>
                <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
                  {h.chefsChoice}
                </span>
              </div>
            </div>
          </div>

          {/* Floating Chef Profile Card */}
          <div className="hidden lg:block absolute top-12 -right-12 w-48 bg-foreground rounded-2xl p-4 shadow-2xl z-30 transform rotate-[3deg] hover:rotate-0 transition-transform duration-500 border border-muted/20">
            <div className="flex items-center justify-between text-background/60 mb-3">
              <Icon name="UserIcon" size={16} />
              <div className="flex gap-2">
                <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
                <Icon name="HeartIcon" size={16} />
              </div>
            </div>
            <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 relative">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1da9ab3ce-1769266959110.png"
                alt="Professional chef in white uniform smiling in modern restaurant kitchen"
                className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs text-background/60 mb-0.5">{h.executiveChef}</p>
              <p className="font-serif text-lg text-background leading-tight">Marco Rossi</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}